import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { loadMyPermissions } from '@/lib/permissions';
import { loadMyYards } from '@/lib/yardStore';

/**
 * Guards routes — redirects to /login if no Supabase session.
 * Also preloads the current user's permissions AND yard (company) list into
 * the client-side caches, so the first page render already knows the active
 * company to scope its queries to.
 */
export default function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'checking' | 'authed' | 'unauthed'>('checking');

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      if (data.session) {
        Promise.all([loadMyPermissions(), loadMyYards()]).finally(() => mounted && setState('authed'));
      } else {
        setState('unauthed');
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      if (session) {
        Promise.all([loadMyPermissions(), loadMyYards()]).finally(() => mounted && setState('authed'));
      } else {
        setState('unauthed');
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (state === 'checking') {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-slate-500">Checking session…</div>
      </div>
    );
  }
  if (state === 'unauthed') return <Navigate to="/login" replace />;
  return <>{children}</>;
}
